import { beginTask, endTask } from 'redux-nprogress';
import { redirect } from 'redux-first-router';
import { selectParams } from '../../../../store/reducers/location.selectors';
import {
  checkSubscriptionStatus,
  getUser,
} from '../../../../store/actions/users.actions.js';
import routes from '../../../routes.global';
import { GO_LOGIN } from '../../../../store/actions/auth.types';
import viewNames from '../../../../store/actions/view.types';
import { RENEW_SUBSCRIPTION_VIEW } from '../../PaymentInformation/module';

// ------------------------------------
// Selectors
// ------------------------------------
export const STORE_KEY = 'accountInfo';
export const selectAccountInfoState = state => state[STORE_KEY];
export const selectAccountInfo = state => selectAccountInfoState(state).accountInfo;
export const selectEmail = state => selectAccountInfoState(state).accountInfo.email;
export const selectUsername = state => selectAccountInfoState(state).accountInfo.uname;
export const selectPreviousPassword = state => selectAccountInfoState(state).previousPassword;
export const selectNewPassword = state => selectAccountInfoState(state).newPassword;
export const selectRetypePassword = state => selectAccountInfoState(state).retypePassword;
export const selectAddress = state => selectAccountInfoState(state).accountInfo.address;
export const selectCity = state => selectAccountInfoState(state).accountInfo.city;
export const selectState = state => selectAccountInfoState(state).accountInfo.state;
export const selectZipcode = state => selectAccountInfoState(state).accountInfo.zipcode;

// ------------------------------------
// Actions & Reducers
// ------------------------------------
const actions = {};

const initialState = {
  hasLoaded: false,
  accountInfo: null,
  previousPassword: '',
  newPassword: '',
  retypePassword: '',
  loadingError: null,
  emailError: false,
  usernameError: false,
  previousPasswordError: false,
  newPasswordError: false,
  retypePasswordError: false,
  addressError: false,
  cityError: false,
  stateError: false,
  zipcodeError: false,
  saveError: null,
  showPasswordInputs: false,
};

export const customReducer = (state = initialState, action) => {
  const handler = actions[action.type];

  return handler ? handler(state, action) : state;
};

export default customReducer;

// ------------------------------------
// Change account email
// ------------------------------------
export const CHANGE_ACCOUNT_EMAIL = `${STORE_KEY}/CHANGE_ACCOUNT_EMAIL`;
export const changeAccountEmail = email => ({
  type: CHANGE_ACCOUNT_EMAIL,
  payload: { email },
});
actions[CHANGE_ACCOUNT_EMAIL] = (state, { payload }) => ({ ...state,
  accountInfo: { ...state.accountInfo,
    email: payload.email,
  },
});

// ------------------------------------
// Change account username
// ------------------------------------
export const CHANGE_ACCOUNT_USERNAME = `${STORE_KEY}/CHANGE_ACCOUNT_USERNAME`;
export const changeAccountUsername = username => ({
  type: CHANGE_ACCOUNT_USERNAME,
  payload: { username },
});
actions[CHANGE_ACCOUNT_USERNAME] = (state, { payload }) => ({ ...state,
  accountInfo: { ...state.accountInfo,
    uname: payload.username,
  },
});

// ------------------------------------
// Reset form state
// ------------------------------------
export const RESET_FORM = `${STORE_KEY}/RESET_FORM`;
export const resetForm = () => ({
  type: RESET_FORM,
  payload: { },
});
actions[RESET_FORM] = (state, { payload }) => ({ ...state,
  hasLoaded: false,
  accountInfo: null,
  newPassword: '',
  retypePassword: '',
  loadingError: null,
  emailError: false,
  usernameError: false,
  newPasswordError: false,
  retypePasswordError: false,
  zipcodeError: false,
  saveError: null,
  showPasswordInputs: false,
});

// ------------------------------------
// Change previous account password
// ------------------------------------
export const CHANGE_PREVIOUS_PASSWORD = `${STORE_KEY}/CHANGE_PREVIOUS_PASSWORD`;
export const changePreviousPassword = previousPassword => ({
  type: CHANGE_PREVIOUS_PASSWORD,
  payload: { previousPassword },
});
actions[CHANGE_PREVIOUS_PASSWORD] = (state, { payload }) => ({ ...state,
  previousPassword: payload.previousPassword,
});

// ------------------------------------
// Change new account password
// ------------------------------------
export const CHANGE_NEW_PASSWORD = `${STORE_KEY}/CHANGE_NEW_PASSWORD`;
export const changeNewPassword = newPassword => ({
  type: CHANGE_NEW_PASSWORD,
  payload: { newPassword },
});
actions[CHANGE_NEW_PASSWORD] = (state, { payload }) => ({ ...state,
  newPassword: payload.newPassword,
});

// ------------------------------------
// Change retype new account password
// ------------------------------------
export const CHANGE_RETYPE_PASSWORD = `${STORE_KEY}/CHANGE_RETYPE_PASSWORD`;
export const changeRetypePassword = retypePassword => ({
  type: CHANGE_RETYPE_PASSWORD,
  payload: { retypePassword },
});
actions[CHANGE_RETYPE_PASSWORD] = (state, { payload }) => ({ ...state,
  retypePassword: payload.retypePassword,
});

// ------------------------------------
// Toggle password form inputs
// ------------------------------------
export const TOGGLE_PASSWORD_INPUTS = `${STORE_KEY}/TOGGLE_PASSWORD_INPUTS`;
export const togglePasswordInputs = () => ({
  type: TOGGLE_PASSWORD_INPUTS,
  payload: { },
});
actions[TOGGLE_PASSWORD_INPUTS] = (state, { payload }) => ({ ...state,
  showPasswordInputs: !state.showPasswordInputs,
});

export const CHANGE_PASSWORD = `${STORE_KEY}/CHANGE_PASSWORD`;
export const changePassword = () => ({
  type: CHANGE_PASSWORD,
  payload: { },
});
actions[CHANGE_PASSWORD] = (state, { payload }) => ({ ...state,
  showPasswordInputs: true,
});

export const TOGGLE_AUTORENEW = `${STORE_KEY}/TOGGLE_AUTORENEW`;
export const toggleAutorenew = newstate => ({
  type: TOGGLE_AUTORENEW,
  payload: { newstate },
});
actions[TOGGLE_AUTORENEW] = (state, { payload }) => ({ ...state,
  accountInfo: { ...state.accountInfo,
    autorenew: payload.newstate,
  },
});

// ------------------------------------
// Change account address
// ------------------------------------
export const CHANGE_ACCOUNT_ADDRESS = `${STORE_KEY}/CHANGE_ACCOUNT_ADDRESS`;
export const changeAccountAddress = address => ({
  type: CHANGE_ACCOUNT_ADDRESS,
  payload: { address },
});
actions[CHANGE_ACCOUNT_ADDRESS] = (state, { payload }) => ({ ...state,
  accountInfo: { ...state.accountInfo,
    address: payload.address,
  },
});

// ------------------------------------
// Change account city
// ------------------------------------
export const CHANGE_ACCOUNT_CITY = `${STORE_KEY}/CHANGE_ACCOUNT_CITY`;
export const changeAccountCity = city => ({
  type: CHANGE_ACCOUNT_CITY,
  payload: { city },
});
actions[CHANGE_ACCOUNT_CITY] = (state, { payload }) => ({ ...state,
  accountInfo: { ...state.accountInfo,
    city: payload.city,
  },
});

// ------------------------------------
// Change account state
// ------------------------------------
export const CHANGE_ACCOUNT_STATE = `${STORE_KEY}/CHANGE_ACCOUNT_STATE`;
export const changeAccountState = state => ({
  type: CHANGE_ACCOUNT_STATE,
  payload: { state },
});
actions[CHANGE_ACCOUNT_STATE] = (state, { payload }) => ({ ...state,
  accountInfo: { ...state.accountInfo,
    state: payload.state,
  },
});

// ------------------------------------
// Change account zipcode
// ------------------------------------
export const CHANGE_ACCOUNT_ZIPCODE = `${STORE_KEY}/CHANGE_ACCOUNT_ZIPCODE`;
export const changeAccountZipcode = zipcode => ({
  type: CHANGE_ACCOUNT_ZIPCODE,
  payload: { zipcode },
});
actions[CHANGE_ACCOUNT_ZIPCODE] = (state, { payload }) => ({ ...state,
  accountInfo: { ...state.accountInfo,
    zipcode: payload.zipcode,
  },
});

// ------------------------------------
// Erase previous data
// ------------------------------------
export const ERASE_PREVIOUS_DATA = `${STORE_KEY}/ERASE_PREVIOUS_DATA`;
export const erasePreviousData = () => ({
  type: ERASE_PREVIOUS_DATA,
});
actions[ERASE_PREVIOUS_DATA] = state => ({ ...state,
  previousPassword: '',
  newPassword: '',
  retypePassword: '',
  showPasswordInputs: false,
});

// ------------------------------------
// Change email error status
// ------------------------------------
export const CHANGE_ACCOUNT_EMAIL_ERROR = `${STORE_KEY}/CHANGE_ACCOUNT_EMAIL_ERROR`;
export const changeAccountEmailError = error => ({
  type: CHANGE_ACCOUNT_EMAIL_ERROR,
  payload: { error },
});
actions[CHANGE_ACCOUNT_EMAIL_ERROR] = (state, { payload }) => ({ ...state,
  emailError: payload.error,
});

// ------------------------------------
// Change username error status
// ------------------------------------
export const CHANGE_ACCOUNT_USERNAME_ERROR = `${STORE_KEY}/CHANGE_ACCOUNT_USERNAME_ERROR`;
export const changeAccountUsernameError = error => ({
  type: CHANGE_ACCOUNT_USERNAME_ERROR,
  payload: { error },
});
actions[CHANGE_ACCOUNT_USERNAME_ERROR] = (state, { payload }) => ({ ...state,
  usernameError: payload.error,
});

// ------------------------------------
// Change previous password error status
// ------------------------------------
export const CHANGE_PREVIOUS_PASSWORD_ERROR = `${STORE_KEY}/CHANGE_PREVIOUS_PASSWORD_ERROR`;
export const changePreviousPasswordError = error => ({
  type: CHANGE_PREVIOUS_PASSWORD_ERROR,
  payload: { error },
});
actions[CHANGE_PREVIOUS_PASSWORD_ERROR] = (state) => ({ ...state,
  previousPasswordError: true,
});

// ------------------------------------
// Change new password error status
// ------------------------------------
export const CHANGE_NEW_PASSWORD_ERROR = `${STORE_KEY}/CHANGE_NEW_PASSWORD_ERROR`;
export const changeNewPasswordError = error => ({
  type: CHANGE_NEW_PASSWORD_ERROR,
  payload: { error },
});
actions[CHANGE_NEW_PASSWORD_ERROR] = (state, { payload }) => ({ ...state,
  newPasswordError: payload.error,
});

// ------------------------------------
// Change retype new password error status
// ------------------------------------
export const CHANGE_RETYPE_PASSWORD_ERROR = `${STORE_KEY}/CHANGE_RETYPE_PASSWORD_ERROR`;
export const changeRetypePasswordError = error => ({
  type: CHANGE_RETYPE_PASSWORD_ERROR,
  payload: { error },
});
actions[CHANGE_RETYPE_PASSWORD_ERROR] = (state, { payload }) => ({ ...state,
  retypePasswordError: payload.error,
});
// ------------------------------------
// Change address error status
// ------------------------------------
export const CHANGE_ACCOUNT_ADDRESS_ERROR = `${STORE_KEY}/CHANGE_ACCOUNT_ADDRESS_ERROR`;
export const changeAccountAddressError = error => ({
  type: CHANGE_ACCOUNT_ADDRESS_ERROR,
  payload: { error },
});
actions[CHANGE_ACCOUNT_ADDRESS_ERROR] = (state, { payload }) => ({ ...state,
  addressError: payload.error,
});

// ------------------------------------
// Change city error status
// ------------------------------------
export const CHANGE_ACCOUNT_CITY_ERROR = `${STORE_KEY}/CHANGE_ACCOUNT_CITY_ERROR`;
export const changeAccountCityError = error => ({
  type: CHANGE_ACCOUNT_CITY_ERROR,
  payload: { error },
});
actions[CHANGE_ACCOUNT_CITY_ERROR] = (state, { payload }) => ({ ...state,
  cityError: payload.error,
});

// ------------------------------------
// Change state error status
// ------------------------------------
export const CHANGE_ACCOUNT_STATE_ERROR = `${STORE_KEY}/CHANGE_ACCOUNT_STATE_ERROR`;
export const changeAccountStateError = error => ({
  type: CHANGE_ACCOUNT_STATE_ERROR,
  payload: { error },
});
actions[CHANGE_ACCOUNT_STATE_ERROR] = (state, { payload }) => ({ ...state,
  stateError: payload.error,
});


// ------------------------------------
// Change zipcode error status
// ------------------------------------
export const CHANGE_ACCOUNT_ZIPCODE_ERROR = `${STORE_KEY}/CHANGE_ACCOUNT_ZIPCODE_ERROR`;
export const changeAccountZipcodeError = error => ({
  type: CHANGE_ACCOUNT_ZIPCODE_ERROR,
  payload: { error },
});
actions[CHANGE_ACCOUNT_ZIPCODE_ERROR] = (state, { payload }) => ({ ...state,
  zipcodeError: payload.error,
});

// ------------------------------------
// Change loading error status
// ------------------------------------
export const CHANGE_ACCOUNT_LOADING_ERROR = `${STORE_KEY}/CHANGE_ACCOUNT_LOADING_ERROR`;
export const changeAccountLoadingError = error => ({
  type: CHANGE_ACCOUNT_LOADING_ERROR,
  payload: { error },
});
actions[CHANGE_ACCOUNT_LOADING_ERROR] = (state, { payload }) => ({ ...state,
  loadingError: payload.error,
});

// ------------------------------------
// Change save error status
// ------------------------------------
export const CHANGE_ACCOUNT_SAVE_ERROR = `${STORE_KEY}/CHANGE_ACCOUNT_SAVE_ERROR`;
export const changeAccountSaveError = error => ({
  type: CHANGE_ACCOUNT_SAVE_ERROR,
  payload: { error },
});
actions[CHANGE_ACCOUNT_SAVE_ERROR] = (state, { payload }) => ({ ...state,
  saveError: payload.error,
});

// ------------------------------------
// Initial Fetch of Account Info data
// ------------------------------------
export const GET_ACCOUNT_INFO_REQUEST = `${STORE_KEY}/GET_ACCOUNT_INFO_REQUEST`;
export const GET_ACCOUNT_INFO_SUCCESS = `${STORE_KEY}/GET_ACCOUNT_INFO_SUCCESS`;
export const GET_ACCOUNT_INFO_ERROR = `${STORE_KEY}/GET_ACCOUNT_INFO_ERROR`;

export const getAccountInfoRequest = () => ({
  type: GET_ACCOUNT_INFO_REQUEST,
  payload: { },
});
export const getAccountInfoSuccess = accountInfo => ({
  type: GET_ACCOUNT_INFO_SUCCESS,
  payload: { accountInfo },
});
export const getAccountInfoError = error => ({
  type: GET_ACCOUNT_INFO_ERROR,
  payload: { error },
});

actions[GET_ACCOUNT_INFO_REQUEST] = state => ({ ...state });
actions[GET_ACCOUNT_INFO_SUCCESS] = (state, { payload }) => ({ ...state,
  hasLoaded: true,
  accountInfo: payload.accountInfo,
});
actions[GET_ACCOUNT_INFO_ERROR] = (state, { payload }) => ({ ...state,
  hasLoaded: false,
  loadingError: payload.error,
});

export const getAccountInfoData = () => async (dispatch, getState, { api }) => {
  try {
    dispatch(beginTask());
    dispatch(getAccountInfoRequest());

    const state = getState();

    dispatch(getAccountInfoSuccess(state.auth.user));
  } catch (error) {
    dispatch(getAccountInfoError(error));
  } finally {
    dispatch(endTask());
  }
};

// ------------------------------------
// Routes / Views
// ------------------------------------
export const ACCOUNT_INFORMATION_VIEW = `${STORE_KEY}/view/ACCOUNT_INFORMATION`;
viewNames[ACCOUNT_INFORMATION_VIEW] = 'myhuddle/AccountInformation';
routes[ACCOUNT_INFORMATION_VIEW] = {
  path: '/myhuddle/account-information',
  thunk: async (dispatch, getState) => {
    const { auth: { loggedIn } } = getState();
    const accountStatus = getState().auth.user.status;
    const isExpired = !(accountStatus === 'A' || !accountStatus);

    // First check if the user is logged in or not
    if (!loggedIn) await dispatch(redirect({ type: GO_LOGIN }));

    // Then check if logged in but account is expired
    else if (loggedIn && isExpired) dispatch(redirect({ type: RENEW_SUBSCRIPTION_VIEW }));

    else {
    // Check if the user has an active account
      await checkSubscriptionStatus(dispatch, getState);
      await dispatch(getAccountInfoData());
    }
  },
};

export const CHANGE_PASSWORD_VIEW = `${STORE_KEY}/view/CHANGE_PASSWORD`;
viewNames[CHANGE_PASSWORD_VIEW] = 'myhuddle/ChangePassword';
export const changePasswordURL = viewNames[CHANGE_PASSWORD_VIEW];
routes[CHANGE_PASSWORD_VIEW] = {
  path: '/myhuddle/change-password',
  thunk: async (dispatch, getState) => {
    const { auth: { loggedIn } } = getState();
    const accountStatus = getState().auth.user.status;
    const isExpired = !(accountStatus === 'A' || !accountStatus);

    // First check if the user is logged in or not
    if (!loggedIn) await dispatch(redirect({ type: GO_LOGIN }));

    // Then check if logged in but account is expired
    else if (loggedIn && isExpired) dispatch(redirect({ type: RENEW_SUBSCRIPTION_VIEW }));

    else {
    // Check if the user has an active account
      await checkSubscriptionStatus(dispatch, getState);
      await dispatch(getAccountInfoData());
    }
  },
};



// WEBPACK FOOTER //
// ./src/app/views/AccountInfo/module/accountInfo.js